"use client";
import React from "react";
import { twMerge } from "tailwind-merge";
import { controlStyles, type Size } from "../../utils/controlStyles";
import { Icon } from "../Icon/Icon";

type ChipVariant = "primary" | "secondary" | "info";
type LegacyChipVariant = "default" | "brand";

export interface ChipProps {
  /** The content to display inside the chip */
  children: React.ReactNode;
  /** Callback when the remove button is clicked */
  onRemove?: () => void;
  /** Accessible label and hover text for the remove control */
  removeLabel?: string;
  /** Size variant of the chip */
  size?: Size | "xs";
  /** Visual variant of the chip */
  /**
   * Visual treatment. Use `primary` for selected/applied chips and
   * `secondary` for available/lower-emphasis chips. `default` and `brand` are
   * deprecated aliases for `secondary` and `primary`, respectively.
   */
  variant?: ChipVariant | LegacyChipVariant;
  /** Whether the chip can be removed (shows X button) */
  isRemovable?: boolean;
  /** Whether the chip is disabled */
  isDisabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Click handler for the chip itself (not the remove button) */
  onClick?: () => void;
}

const chipVariantStyles = {
  primary: "bg-action-primary/10 text-action-primary border border-action-primary/20 hover:bg-action-primary/15",
  secondary:
    "bg-action-secondary text-[color:var(--color-action-secondary-text)] border border-border-muted hover:bg-action-secondary-hover",
  info: "bg-feedback-info-background text-feedback-info-text border border-feedback-info-border hover:bg-feedback-info-background/80",
} satisfies Record<ChipVariant, string>;

function normalizeVariant(variant: ChipProps["variant"]): ChipVariant {
  if (!variant || variant === "default") {
    return "secondary";
  }

  if (variant === "brand") {
    return "primary";
  }

  return variant;
}

const chipSizeStyles = {
  xs: "px-1.5 py-[2px] gap-0.5",
  sm: "px-2 py-0.5 gap-1 leading-4",
  md: "px-3 py-1.5 gap-1.5",
  lg: "px-3 py-2 gap-2",
  xl: "px-4 py-2.5 gap-2",
};

const disabledStyles = "opacity-75 cursor-not-allowed pointer-events-none";

function getChipStyles(props: {
  variant: ChipVariant;
  size: NonNullable<ChipProps["size"]>;
  isDisabled?: boolean;
  isClickable?: boolean;
  className?: string;
}) {
  const { variant, size, isDisabled, isClickable, className } = props;

  // Get text size - xs uses text-xs, otherwise use controlStyles
  const textSize = size === "xs" ? "text-xs" : controlStyles[size].text;

  return twMerge(
    // Base styles
    "inline-flex items-center justify-center rounded-full font-medium transition-colors",
    "whitespace-nowrap select-none",

    // Size styles
    chipSizeStyles[size],
    textSize,

    // Variant styles
    chipVariantStyles[variant],

    // Interactive styles
    isClickable && !isDisabled && "cursor-pointer",

    // Disabled styles
    isDisabled && disabledStyles,

    // Custom className
    className
  );
}

/**
 * Chip
 *
 * A compact element that represents an input, attribute, or action.
 * Can be removable with an X button and supports different variants and sizes.
 */
export function Chip({
  children,
  onRemove,
  removeLabel = "Remove",
  size = "md",
  variant = "secondary",
  isRemovable = true,
  isDisabled = false,
  className,
  onClick,
}: ChipProps) {
  const isClickable = !!onClick && !isDisabled;
  const showRemoveButton = isRemovable && onRemove && !isDisabled;

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onRemove && !isDisabled) {
      onRemove();
    }
  };

  const handleChipClick = () => {
    if (onClick && !isDisabled) {
      onClick();
    }
  };

  const iconSize = size === "xs" ? 10 : size === "sm" ? 12 : size === "md" ? 14 : 16;

  const removeControlStyles = twMerge(
    "flex-shrink-0 rounded-full p-0.5 hover:bg-text-primary/10 transition-colors cursor-pointer",
    size === "sm" && "inline-flex size-4 items-center justify-center p-0 leading-none"
  );

  // Render remove control as span when chip is clickable (button), button when chip is static (span)
  const removeControl = showRemoveButton ? (
    isClickable ? (
      <span
        onClick={handleRemoveClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleRemoveClick(e as unknown as React.MouseEvent);
          }
        }}
        role="button"
        tabIndex={0}
        className={removeControlStyles}
        aria-label={removeLabel}
        title={removeLabel}
      >
        <Icon name="X" size={iconSize} className="text-current opacity-70 hover:opacity-100" />
      </span>
    ) : (
      <button
        type="button"
        onClick={handleRemoveClick}
        className={removeControlStyles}
        aria-label={removeLabel}
        title={removeLabel}
        tabIndex={-1}
      >
        <Icon name="X" size={iconSize} className="text-current opacity-70 hover:opacity-100" />
      </button>
    )
  ) : null;

  const chipContent = (
    <>
      <span className="truncate">{children}</span>
      {removeControl}
    </>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        onClick={handleChipClick}
        className={getChipStyles({
          variant: normalizeVariant(variant),
          size,
          isDisabled,
          isClickable,
          className,
        })}
        disabled={isDisabled}
      >
        {chipContent}
      </button>
    );
  }

  return (
    <span
      className={getChipStyles({
        variant: normalizeVariant(variant),
        size,
        isDisabled,
        isClickable,
        className,
      })}
    >
      {chipContent}
    </span>
  );
}
